import { computed, watch, ref } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useRoute } from 'vue-router'

export const useCello = () => {
  const userStore = useUserStore()
  const globalStore = useGlobalStore()
  const configStore = useConfigStore()
  const route = useRoute()

  const celloToken = computed(() => configStore.config?.celloToken)

  // Desired position — kept in sync by App.vue via updateCelloPosition()
  const _bottom = ref('18px')
  const _right = ref('90px') // default: 18px (zendesk right) + 48px (button) + 24px (gap)

  let _observer = null

  const getFab = () => {
    const host = document.getElementById('cello-widget-app')
    return host?.shadowRoot?.querySelector('.fab-enter-done') ?? null
  }

  const applyStyle = () => {
    const fab = getFab()
    if (!fab) return

    // Disconnect before writing to avoid triggering ourselves
    _observer?.disconnect()

    fab.style.setProperty('position', 'fixed', 'important')
    fab.style.setProperty('left', 'auto', 'important')
    fab.style.setProperty('right', _right.value, 'important')
    fab.style.setProperty('bottom', _bottom.value, 'important')

    // Reconnect so future Cello-initiated changes are caught
    _reconnect()
  }

  const _reconnect = () => {
    if (!_observer) return
    const host = document.getElementById('cello-widget-app')
    if (!host?.shadowRoot) return
    _observer.observe(host.shadowRoot, {
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
    })
  }

  const startObserver = () => {
    const host = document.getElementById('cello-widget-app')
    if (!host?.shadowRoot || _observer) return
    _observer = new MutationObserver(() => applyStyle())
    _reconnect()
  }

  // Called from App.vue's setZendeskPosition so Cello mirrors Zendesk's vertical
  // position and stays 24px to its left (accounting for the 48px button width).
  const updateCelloPosition = (bottom, zendeskRight) => {
    _bottom.value = bottom
    _right.value = `${parseInt(zendeskRight) + 48 + 24}px`
    applyStyle()
  }

  watch(celloToken, () => {
    if (!celloToken.value || globalStore.celloAlreadyBooted) return

    window.cello = window.cello || { cmd: [] }

    window.cello.cmd.push(async function (cello) {
      try {
        await cello.boot({
          productId: import.meta.env.VITE_CELLO_PRODUCT_ID,
          token: celloToken.value,
          language: 'en',
          productUserDetails: {
            firstName: userStore.user?.first_name,
            lastName: userStore.user?.last_name,
            fullName: `${userStore.user?.first_name} ${userStore.user?.last_name}`,
            email: userStore.user?.email,
          },
        })

        globalStore.celloAlreadyBooted = true
        startObserver()
        // fab-enter-done is set after the enter animation completes — retry until it exists
        applyStyle()
        setTimeout(() => applyStyle(), 300)
        setTimeout(() => applyStyle(), 800)

        route.name !== 'Homepage' && window.Cello && window.Cello('hide')
      } catch (error) {
        console.error('Failed to boot cello:', error)
      }
    })
  })

  watch(() => globalStore.sidebar, (sidebarOpen) => {
    if (!window.Cello) return
    const isMobile = window.innerWidth < 768
    if (isMobile && sidebarOpen) {
      window.Cello('hide')
    } else {
      route.name !== 'Homepage' && window.Cello('show')
    }
  })

  return { updateCelloPosition }
}
